import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconCurrentLocation, IconPlus } from "@tabler/icons-react";
import { addShooting, getAthleteById, getShootingsByAthlete } from "../../db";
import LoadinPage from "../../Components/LoadingPage";
import ShootingPreview5 from "../../Components/Shooting/ShootingPreview5";

const Shooting = () => {
    const { id } = useParams(); // ✅ Get athlete ID from URL
    const navigate = useNavigate(); // ✅ Redirect if athlete is not found
    const { t } = useTranslation();

    const [athlete, setAthlete] = useState(null);
    const [shootings, setShootings] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ Fetch athlete details
    useEffect(() => {
        const fetchAthlete = async () => {
            const foundAthlete = await getAthleteById(Number(id)); // Ensure ID is a number
            setLoading(false);

            if (foundAthlete) {
                setAthlete(foundAthlete);
            } else {
                navigate("/athletes", { state: { error: t("athletesDetail.notFound") } });
            }
        };

        fetchAthlete();
    }, [id, navigate, t]);

    // ✅ Fetch shootings after athlete is set
    useEffect(() => {
        if (!athlete) return; // Ensure athlete exists before fetching shootings

        const fetchShootings = async () => {
            const data = await getShootingsByAthlete(Number(athlete.id));
            setShootings(data);
        };

        fetchShootings();
    }, [athlete]);

    // ✅ Handle creating a new shooting session
    const handleNewShooting = async () => {
        if (!athlete) return; // Ensure athlete exists before adding shooting

        try {
            const shootingId = await addShooting({ athlete: athlete.id }); // ✅ Use `athlete.id`
            navigate(`/shooting/${shootingId}`);
        } catch (error) {
            console.error("Error adding shooting:", error);
        }
    };

    const groupShootingsByDate = (shootings) => {
        return shootings
            .sort((a, b) => b.date - a.date) // ✅ Sort newest → oldest
            .reduce((acc, shooting) => {

                const dateKey = new Intl.DateTimeFormat("cs-CZ", { day: "2-digit", month: "2-digit", year: "numeric" })
                    .format(new Date(shooting.date * 1000));

                if (!acc[dateKey]) {
                    acc[dateKey] = [];
                }

                acc[dateKey].push(shooting);
                return acc;
            }, {});
    };

    const groupedShootings = groupShootingsByDate(shootings);

    if (loading) return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to={`/athletes`}>
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("athletesDetail.backToDetail")}</span>
                    </Link>
                </div>
                <div className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                </div>
                <div className="shrink-0 w-10"></div>
            </div>

            <div className="grow overflow-y-auto overflow-x-hidden">
                <LoadinPage />
            </div>
        </div>
    );

    return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center pb-4 w-full">
                <div className="shrink-0 w-10">
                    <Link to={`/athletes/${athlete.id}`}>
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("athletesDetail.backToDetail")}</span>
                    </Link>
                </div>
                <div className="grow flex items-center justify-center gap-2">
                    <IconCurrentLocation className="w-4 h-auto" />
                    <h1 className="text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                        {athlete.nickname || `${athlete.firstname} ${athlete.lastname}`}
                    </h1>
                </div>
                <div className="shrink-0 w-10"></div>
            </div>

            {/* ✅ Shooting List */}
            <div>
                {shootings.length === 0 ? (
                    <p>{t("athletesShooting.noShootings")}</p>
                ) : (
                    <div className="flex flex-col gap-6 px-2">
                        {Object.entries(groupedShootings).map(([date, shootings]) => (
                            <div key={date} className="flex flex-col gap-2 text-xs">
                                <h2 className="text-lg font-bold">{date}</h2>
                                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                                    {shootings.map((shooting) => (
                                        <Link className={(shooting.type === 'race' ? 'button-primary' : 'button-secondary')} to={`/shooting/${shooting.id}`} key={shooting.id} >
                                            {shooting.points && (shooting.points.length === 5) && (
                                                <div>
                                                    <ShootingPreview5 shooting={shooting} />
                                                </div>
                                            )}
                                            <div className="aspect-square h-4 flex items-center justify-center bg-[var(--dark-color)] border border-[var(--dark-color)] text-[var(--light-color)] text-[0.5rem] leading-none">{t("shooting." + shooting.position)}</div>
                                            <div>
                                                {shooting.date && (() => {
                                                    const date = new Date(shooting.date * 1000);
                                                    const hours = String(date.getHours()).padStart(2, "0");
                                                    const seconds = String(date.getSeconds()).padStart(2, "0");

                                                    return `${hours}:${seconds}`;
                                                })()}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button
                onClick={handleNewShooting}
                className="p-4 rounded-full bg-[var(--dark-color)] text-[var(--light-color)] absolute right-2 bottom-2"
            >
                <IconPlus className="w-6 h-auto" />
                <span className="sr-only">{t("athletesShooting.addNew")}</span>
            </button>
        </div>
    );
};

export default Shooting;